import { useState } from 'react';
import { useForm } from "react-hook-form";
import { z } from 'zod';
import { zodResolver } from "@hookform/resolvers/zod";

import { useNotification, useAutocomplete } from '@/hooks';
import { FormAutocompleteInput, FormSubmitButton } from '@/components';
import { useVehicles } from '@/pages/Fleet/Vehicles/hooks';
import type { Vehicle } from '@/pages/Fleet/Vehicles/models';
import { NotificationSeverity } from '@/components/notifications/Notification';

import { useSetVehicleToAssignmentMutation } from '../services/assignments.service';

import styles from "./SetVehicle.module.css";

const setVehicleSchema = z.object({
  vehicleId: z.string().uuid(),
});

type SetVehicleSchema = z.infer<typeof setVehicleSchema>;

interface Props {
  assignmentId: string;
  onClose: () => void;
};

const SetVehicleForm = ({ assignmentId, onClose }: Props) => {

  const { showNotification } = useNotification();
  const [ vehicleSearch, setVehicleSearch ] = useState('');

  const [ vehicleInfo, setVehicleInfo ] = useState<Vehicle | null>(null);

  const [ setVehicleToAssignment ] = useSetVehicleToAssignmentMutation();

  const { handleSubmit, control, reset, formState, setValue } = useForm<SetVehicleSchema>({
    resolver: zodResolver(setVehicleSchema),
    mode: 'onChange',
    shouldFocusError: false,
    defaultValues: {
      vehicleId: '',
    },
  });

  const { vehicles } = useVehicles({
    filters: { plate: vehicleSearch },
    page: 1,
    pageSize: 10,
  });

  const { options: vehiclesOptions } = useAutocomplete({
    data: vehicles,
    getLabel: (vehicle: Vehicle) => `${vehicle.description} - ${vehicle.plate}`,
  });

  const onSubmit = async (data: SetVehicleSchema) => {
    const payload = { ...data, assignmentId };
    try {
      await setVehicleToAssignment(payload).unwrap();
      showNotification(
        'Vehículo asignado correctamente',
        NotificationSeverity.success
      );
      reset();
      onClose();
    } catch (error: any) {
      if (error.data?.confirmable) {
        confirm('El tipo de vehículo no es válido. ¿Deseas forzar la asignación?') &&
          setVehicleToAssignment({ ...payload, force: true })
          .then(() => {
            showNotification('Vehículo asignado correctamente', NotificationSeverity.success);
            reset();
            onClose();
          })
          .catch(() => showNotification('Ocurrió un error al asignar el vehículo. Inténtalo nuevamente.', NotificationSeverity.error));
      } else {
        showNotification('Ocurrió un error al asignar el vehículo. Inténtalo nuevamente.', NotificationSeverity.error);
      }
    }
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
      <h1 className={styles.h1}>Asignar un vehículo</h1>
      <FormAutocompleteInput
        name={'vehicleId'}
        control={control}
        placeholder={'Seleccionar vehículo'}
        onChange={(newValue) => {
          setVehicleInfo(newValue?.data);
          setValue('vehicleId', newValue?.data.id, {
            shouldValidate: true,
          });
        }}
        onInputChange={setVehicleSearch}
        options={vehiclesOptions}
        required
      />
      {vehicleInfo && (
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div className={styles.dataSelected}>
            <div className={styles.dataSelectedItem}>
              <strong>Descripción:</strong>
              <p>{vehicleInfo.description}</p>
            </div>
            <div className={styles.dataSelectedItem}>
              <strong>Matrícula:</strong>
              <p>{vehicleInfo.plate}</p>
            </div>
            <div className={styles.dataSelectedItem}>
              <strong>Tipo de vehículo:</strong>
              <p>{vehicleInfo.type.name}</p>
            </div>
          </div>
        </div>
      )}
      <FormSubmitButton
        label='Asignar vehículo'
        action={handleSubmit(onSubmit)}
        disabled={!formState.isValid}
        isLoading={formState.isSubmitting}
      />
    </form>
  );
};

export default SetVehicleForm;
